import { useCallback } from 'react'
import { FilterState, FilterableField } from '@/components/filters/types'
import CollapsibleSection from './CollapsibleSection'
import AudienceSection from './AudienceSection'
import TriggerSection from './TriggerSection'
import PostConfigPanel from './PostConfigPanel'
import PostPreview from './PostPreview'
import { uploadImage } from '@/lib/imageUpload'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface PageRule {
  match_type: 'exact' | 'contains' | 'starts_with' | 'regex'
  value: string
}

interface PostFormData {
  name: string
  body: string
  post_format: 'small' | 'large'
  reply_type: 'none' | 'reactions' | 'text'
  reaction_emojis: string[]
  delivery_style: 'badge' | 'snippet' | 'full'
  sent_by_agent_id: number
  audience_type: 'dynamic' | 'fixed'
  audience_rules: FilterState
  trigger_type: 'event' | 'scheduled' | 'page' | 'all_pages'
  trigger_event: string
  trigger_page_rules: PageRule[]
  scheduled_at: string
  delay_seconds: number
  frequency_cap: string
  priority: number
  collect_email: boolean
}

interface PostFormLayoutProps {
  data: PostFormData
  setData: <K extends keyof PostFormData>(key: K, value: PostFormData[K]) => void
  errors: Partial<Record<string, string>>
  agents: Agent[]
  maxReactionEmojis: number
  filterableFields: FilterableField[]
  addPageRule: () => void
  updatePageRule: (index: number, field: keyof PageRule, value: string) => void
  removePageRule: (index: number) => void
}

export default function PostFormLayout({
  data,
  setData,
  errors,
  agents,
  maxReactionEmojis,
  filterableFields,
  addPageRule,
  updatePageRule,
  removePageRule,
}: PostFormLayoutProps) {
  const selectedAgent = agents.find((a) => a.id === data.sent_by_agent_id)

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadImage(file)
    return result.url
  }, [])

  return (
    <div className="space-y-6">
      <CollapsibleSection title="Conteúdo">
        <div className="border-t border-gray-200">
          <div className="flex flex-1" style={{ minHeight: '500px' }}>
            <PostConfigPanel
              agents={agents}
              sentByAgentId={data.sent_by_agent_id}
              onAgentChange={(id) => setData('sent_by_agent_id', id)}
              postFormat={data.post_format as 'small' | 'large'}
              onFormatChange={(v) => setData('post_format', v)}
              replyType={data.reply_type}
              onReplyTypeChange={(v) => setData('reply_type', v)}
              deliveryStyle={data.delivery_style}
              onDeliveryStyleChange={(v) => setData('delivery_style', v)}
              collectEmail={data.collect_email}
              onCollectEmailChange={(v) => setData('collect_email', v)}
            />
            <div className="flex-1 p-4 bg-gray-50">
              <PostPreview
                body={data.body}
                onBodyChange={(v) => setData('body', v)}
                postFormat={data.post_format as 'small' | 'large'}
                replyType={data.reply_type}
                reactionEmojis={data.reaction_emojis}
                maxReactionEmojis={maxReactionEmojis}
                onReactionEmojisChange={(emojis) => setData('reaction_emojis', emojis)}
                agent={selectedAgent}
                onImageUpload={handleImageUpload}
              />
              {errors.body && (
                <p className="mt-2 text-sm text-red-600">{errors.body}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Público">
        <AudienceSection
          audienceType={data.audience_type}
          onAudienceTypeChange={(v) => setData('audience_type', v)}
          audienceRules={data.audience_rules}
          onAudienceRulesChange={(state) => setData('audience_rules', state)}
          filterableFields={filterableFields}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Gatilho">
        <TriggerSection
          idPrefix="post"
          triggerType={data.trigger_type}
          onTriggerTypeChange={(v) => setData('trigger_type', v)}
          triggerEvent={data.trigger_event}
          onTriggerEventChange={(v) => setData('trigger_event', v)}
          triggerPageRules={data.trigger_page_rules}
          addPageRule={addPageRule}
          updatePageRule={updatePageRule}
          removePageRule={removePageRule}
          scheduledAt={data.scheduled_at}
          onScheduledAtChange={(v) => setData('scheduled_at', v)}
          delaySeconds={data.delay_seconds}
          onDelaySecondsChange={(v) => setData('delay_seconds', v)}
          frequencyCap={data.frequency_cap}
          onFrequencyCapChange={(v) => setData('frequency_cap', v)}
          errors={errors}
        />
      </CollapsibleSection>
    </div>
  )
}
